import React, { useState, useRef, useEffect } from 'react';
import { useChatStore } from '../../hooks/useChatStore';
import { useAuthStore } from '../../hooks/useAuthStore';
import MessageBubble from './MessageBubble';
import EmojiPicker from './EmojiPicker';
import UserInfoPanel from './UserInfoPanel';
import ChannelInfoPanel from './ChannelInfoPanel';
import GroupInfoPanel from './GroupInfoPanel';
import { PushNav, playBlaBlaSound, showBrowserNotification } from '../../services/notifications';
import VideoRecorderModal from './VideoRecorderModal';
import api, { absUrl } from '../../services/api';
import '../../styles/chat.css';
import '../../styles/call.css';

const chatScrollPositions = {};

const RANDOM_EMOJIS = ['😀','😎','🤩','🥳','😇','🤗','🤔','😏','🤓','🦊','🐱','🐶','🦁','🐼','🐸','🦄','🌟','🔥','💎','🎵','🚀','⚡','🎯','🎮','🏆','🌈','🎨','🍕','☕','🌺'];

const MONTH_NAMES = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];

const MASHAGPT_URL = 'https://mashagpt.link/FW6jPV7Bf';

const AI_LIMIT_TEXT = 'Вы достигли максимального лимита для бесплатной генерации картинок в месяц. Если не хотите ждать месяц до сброса счётчика, то рекомендуем зарегистрироваться у нашего партнёра MashaGPT и продолжить генерировать картинки через их сайт!';

function getHistoryWarning() {
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysUntilDeletion = daysInMonth - now.getDate() + 1;
  if (daysUntilDeletion > 2) return null;
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevDays = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  const period = `1-${prevDays} ${MONTH_NAMES[prev.getMonth()]}`;
  return `!!!ВНИМАНИЕ!!!История чата за период ${period} будет удалена через 2 дня`;
}

export function getAvatarEmoji(name) {
  if (!name) return RANDOM_EMOJIS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return RANDOM_EMOJIS[Math.abs(hash) % RANDOM_EMOJIS.length];
}

function getDateLabel(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.floor((today - msgDate) / 86400000);
  if (diff === 0) return 'Сегодня';
  if (diff === 1) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

function detectFileType(file) {
  if (file.type.startsWith('image/')) return 'IMAGE';
  if (file.type.startsWith('video/')) return 'VIDEO';
  if (file.type.startsWith('audio/')) return 'AUDIO';
  return 'FILE';
}

export default function ChatWindow({ activeAIChat, onOpenAI }) {
  const { activeChat, chats, messages, sendMessage, sendFileMessage, typingUsers, onlineUsers, emitTyping, forwardMessage, pinnedMessages, fetchPinnedMessages, unpinMessage, scheduleMessage, fetchScheduledMessages, fetchChats, toggleMute, fetchMessages, fetchedChats, fetchingChats } = useChatStore();
  const { user } = useAuthStore();
  const [input, setInput] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const inputRef = useRef(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [aiMessages, setAiMessages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLimit, setAiLimit] = useState({ remaining: 5, limited: false, unlimited: false });
  const [aiModels, setAiModels] = useState([]);
  const [aiModel, setAiModel] = useState('');
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [showChannelInfo, setShowChannelInfo] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [recording, setRecording] = useState(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const [forwardItem, setForwardItem] = useState(null);
  const [forwarding, setForwarding] = useState(false);
  const messagesEndRef = useRef(null);
  const aiPendingJobRef = useRef(null);
  const knownAssistantIdsRef = useRef(new Set());
  const knownImageUrlsRef = useRef(new Set());
  const activeAIChatRef = useRef(activeAIChat);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const initialScrollDone = useRef(false);

  const chat = chats.find((c) => c.id === activeChat);
  const chatMessages = messages[activeChat] || [];
  const pinnedList = pinnedMessages[activeChat] || [];
  const typingUserId = typingUsers[activeChat];
  const isOnline = chat?.participants?.some((p) => onlineUsers[p.userId]);
  const isChannelAdmin = chat?.isChannel && chat?.participants?.find((p) => p.userId === user?.id)?.role === 'ADMIN';
  const canWrite = (!chat?.isChannel || isChannelAdmin || chat?.allowComments) && !chat?.isBlocked && !chat?.theyBlockedMe;

  useEffect(() => {
    activeAIChatRef.current = activeAIChat;
  }, [activeAIChat]);

  useEffect(() => {
    if (activeChat) fetchPinnedMessages(activeChat);
    // При смене чата закрываем всплывающие панели (эмодзи/стикеры, расписание),
    // иначе они остаются висеть над другим чатом.
    setShowEmoji(false);
    setShowSchedule(false);
    setMentionOpen(false);
  }, [activeChat]);

  // Fallback: гарантированно загружаем сообщения открытого чата (даже если
  // fetch из setActiveChat не завершился), без дублирования запроса.
  useEffect(() => {
    if (activeChat && !fetchedChats[activeChat] && !fetchingChats[activeChat]) fetchMessages(activeChat);
  }, [activeChat]);

  useEffect(() => {
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, aiMessages]);

  useEffect(() => {
    const pending = window.__pendingMsg;
    if (!pending || pending.chatId !== activeChat || chatMessages.length === 0) return;
    window.__pendingMsg = null;
    initialScrollDone.current = true;
    const el = document.querySelector(`[data-msg-id="${pending.messageId}"]`);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('msg-highlight');
      setTimeout(() => el.classList.remove('msg-highlight'), 2200);
      setIsNearBottom(false);
    }
  }, [activeChat, chatMessages]);

  useEffect(() => {
    const onScrollToMsg = () => {
      const pending = window.__pendingMsg;
      if (!pending || pending.chatId !== activeChat) return;
      window.__pendingMsg = null;
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-msg-id="${pending.messageId}"]`);
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          el.classList.add('msg-highlight');
          setTimeout(() => el.classList.remove('msg-highlight'), 2200);
          setIsNearBottom(false);
        }
      });
    };
    window.addEventListener('scrollToMessage', onScrollToMsg);
    return () => window.removeEventListener('scrollToMessage', onScrollToMsg);
  }, [activeChat]);

  useEffect(() => {
    initialScrollDone.current = false;
    if (activeChat) {
      const fromPush = PushNav.chatId === activeChat;
      PushNav.chatId = null;
      if (fromPush) {
        initialScrollDone.current = true;
        setIsNearBottom(true);
      }
    }
  }, [activeChat]);

  useEffect(() => {
    const el = chatMessagesRef.current;
    if (!el || initialScrollDone.current) return;
    if (chatMessages.length === 0) return;
    initialScrollDone.current = true;
    const saved = chatScrollPositions[activeChat];
    if (saved != null && saved > 0) {
      el.scrollTop = saved;
      setIsNearBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 120);
    } else {
      el.scrollTop = el.scrollHeight;
      setIsNearBottom(true);
    }
  }, [activeChat, chatMessages.length]);

  useEffect(() => {
    return () => {
      const el = chatMessagesRef.current;
      if (el && activeChat) {
        chatScrollPositions[activeChat] = el.scrollTop;
      }
    };
  }, [activeChat]);

  useEffect(() => {
    if (activeAIChat) {
      const loadAIHistory = async () => {
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const res = await api.get('/ai/history');
            const mapped = res.data.map((m) => ({
              id: m.id,
              role: m.role,
              content: m.content,
              imageUrl: m.imageUrl,
              timestamp: m.createdAt,
            }));
            knownAssistantIdsRef.current = new Set(res.data.filter((m) => m.role === 'assistant').map((m) => m.id));
            knownImageUrlsRef.current = new Set(res.data.filter((m) => m.imageUrl).map((m) => m.imageUrl));
            setAiMessages(mapped);
            return;
          } catch (e) {
            if (attempt >= 2) return;
            await new Promise((r) => setTimeout(r, 1500));
          }
        }
      };
      loadAIHistory();
      fetchAiLimit();
      fetchAiModels();
    }
  }, [activeAIChat]);

  const fetchAiModels = async () => {
    try {
      const res = await api.get('/ai/models');
      const list = res.data || [];
      setAiModels(list);
      const saved = localStorage.getItem('trandi_ai_model');
      if (saved && list.some((m) => m.id === saved)) {
        setAiModel(saved);
      } else if (list.length > 0) {
        setAiModel(list[0].id);
      }
    } catch (e) {
      console.error('Не удалось загрузить модели ИИ', e);
    }
  };

  const fetchAiLimit = async () => {
    try {
      const res = await api.get('/ai/limit');
      setAiLimit(res.data || { remaining: 5, limited: false, unlimited: false });
    } catch (e) {}
  };

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      clearInterval(recordingTimerRef.current);
    };
  }, []);

  const handleSend = async () => {
    if (!input.trim()) return;
    await sendMessage(activeChat, input, 'TEXT');
    setInput('');
    setMentionOpen(false);
    setMentionQuery('');
  };

  const handleKeyDown = (e) => {
    if (mentionOpen && filteredMentions.length > 0) {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); insertMention(filteredMentions[0]); return; }
      if (e.key === 'Tab') { e.preventDefault(); insertMention(filteredMentions[0]); return; }
      if (e.key === 'Escape') { setMentionOpen(false); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const mentionCandidates = (chat?.isGroup && !chat?.isChannel && chat?.participants?.length)
    ? chat.participants.filter((p) => p.userId !== user?.id)
    : [];
  const filteredMentions = mentionQuery
    ? mentionCandidates.filter((p) => (p.displayName || p.username || '').toLowerCase().includes(mentionQuery.toLowerCase()))
    : mentionCandidates;

  const updateMentionState = (value, caret) => {
    if (!mentionCandidates.length) { setMentionOpen(false); return; }
    const before = value.slice(0, caret);
    const idx = before.lastIndexOf('@');
    if (idx === -1) { setMentionOpen(false); return; }
    const after = value.slice(idx + 1, caret);
    if (/[\s\p{P}]/u.test(after)) { setMentionOpen(false); return; }
    setMentionQuery(after);
    setMentionOpen(true);
  };

  const insertMention = (p) => {
    const name = p.username || p.displayName || 'пользователь';
    const caret = inputRef.current?.selectionStart ?? input.length;
    const before = input.slice(0, caret);
    const idx = before.lastIndexOf('@');
    const newInput = (idx === -1 ? input : input.slice(0, idx)) + '@' + name + ' ' + input.slice(caret);
    setInput(newInput);
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const handleScheduleSend = async () => {
    if (!input.trim() || !scheduleAt) return;
    const scheduledAt = new Date(scheduleAt).toISOString();
    await scheduleMessage(activeChat, input, scheduledAt);
    setInput('');
    setMentionOpen(false);
    setMentionQuery('');
    setScheduleAt('');
    setShowSchedule(false);
    fetchScheduledMessages();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPendingFile({ file, url: URL.createObjectURL(file), type: detectFileType(file) });
    }
    e.target.value = '';
  };

  const confirmPendingFile = async () => {
    if (!pendingFile) return;
    const scheduledAt = showSchedule && scheduleAt ? new Date(scheduleAt).toISOString() : null;
    await sendFileMessage(activeChat, pendingFile.file, pendingFile.type, scheduledAt);
    if (scheduledAt) {
      setScheduleAt('');
      setShowSchedule(false);
      alert('⏰ Файл сохранён в «Запланированные»');
    }
    setPendingFile(null);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setPendingFile({ file, url: URL.createObjectURL(file), type: detectFileType(file) });
    }
  };

  const startRecording = async (type) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(type === 'audio' ? { audio: true } : { audio: true, video: true });
      streamRef.current = stream;
      mediaChunksRef.current = [];
      const mimeType = type === 'audio' ? 'audio/webm' : 'video/webm';
      let recorder;
      try {
        recorder = new MediaRecorder(stream, { mimeType });
      } catch (e) {
        recorder = new MediaRecorder(stream);
      }
      recorder.ondataavailable = (e) => { if (e.data.size > 0) mediaChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(mediaChunksRef.current, { type: mimeType });
        const file = new File([blob], `${type}_${Date.now()}.webm`, { type: mimeType });
        const scheduledAt = showSchedule && scheduleAt ? new Date(scheduleAt).toISOString() : null;
        sendFileMessage(activeChat, file, type === 'audio' ? 'AUDIO_MESSAGE' : 'VIDEO_MESSAGE', scheduledAt);
        if (scheduledAt) {
          setScheduleAt('');
          setShowSchedule(false);
          alert('⏰ Сообщение сохранено в «Запланированные»');
        }
        clearInterval(recordingTimerRef.current);
        setRecordingSeconds(0);
        setRecording(null);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(type);
      setRecordingSeconds(0);
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
    } catch (e) {
      console.error('Не удалось начать запись:', e);
      alert('Не удалось получить доступ к микрофону/камере');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const handleAISend = async () => {
    if (!input.trim() || aiLoading) return;
    const userMsg = { id: Date.now(), role: 'user', content: input, timestamp: new Date().toISOString() };
    setAiMessages((prev) => [...prev, userMsg]);
    api.post('/ai/history', { role: 'user', content: input }).catch(() => {});
    const prompt = input;
    setInput('');
    setAiLoading(true);
    try {
      // Генерация выполняется на сервере в фоне: клиент получает jobId и ждёт
      // WebSocket-событие ai_generation_done / ai_generation_failed.
      const res = await api.post('/ai/generate-image', { prompt, model: aiModel || undefined });
      aiPendingJobRef.current = res.data?.jobId || null;
    } catch (err) {
      if (err.response?.status === 429) {
        const limitMsg = {
          id: Date.now() + 1,
          role: 'system',
          content: AI_LIMIT_TEXT,
          timestamp: new Date().toISOString(),
        };
        setAiMessages((prev) => [...prev, limitMsg]);
        setAiLimit({ remaining: 0, limited: true, unlimited: false });
      } else {
        const errMsg = {
          id: Date.now() + 1,
          role: 'assistant',
          content: 'Ошибка запуска генерации. Попробуйте снова.',
          timestamp: new Date().toISOString(),
        };
        setAiMessages((prev) => [...prev, errMsg]);
      }
      setAiLoading(false);
    }
    fetchAiLimit();
  };

  // Страховка для генерации картинки: если WS-событие потеряно (обрыв соединения),
// подхватываем результат из истории ИИ поллингом.
  useEffect(() => {
    if (!aiLoading) return;
    const started = Date.now();
    const id = setInterval(async () => {
      try {
        const res = await api.get('/ai/history');
        const items = res.data || [];
        const known = knownAssistantIdsRef.current;
        const knownUrls = knownImageUrlsRef.current;
        const freshAssistant = items.filter((m) => m.role === 'assistant' && !known.has(m.id) && !(m.imageUrl && knownUrls.has(m.imageUrl)));
        if (freshAssistant.length > 0) {
          knownAssistantIdsRef.current = new Set([...known, ...freshAssistant.map((m) => m.id)]);
          knownImageUrlsRef.current = new Set([...knownUrls, ...freshAssistant.map((m) => m.imageUrl).filter(Boolean)]);
          setAiMessages((prev) => [...prev, ...freshAssistant.map((m) => ({
            id: m.id, role: m.role, content: m.content, imageUrl: m.imageUrl, timestamp: m.createdAt,
          }))]);
          setAiLoading(false);
        } else if (Date.now() - started > 240000) {
          setAiLoading(false);
        }
      } catch (e) {}
    }, 5000);
    return () => clearInterval(id);
  }, [aiLoading]);

  const notifyIfAway = () => {
    if (activeAIChatRef.current) return;
    playBlaBlaSound();
    showBrowserNotification('AI Ассистент', 'Генерация изображения завершена', '__ai__');
  };
  const onGenDone = (e) => {
    const { imageUrl, prompt } = e.detail || {};
    const aiMsg = {
      id: Date.now() + 1,
      role: 'assistant',
      content: prompt || 'Изображение готово',
      imageUrl: imageUrl || null,
      timestamp: new Date().toISOString(),
    };
    setAiMessages((prev) => {
      if (imageUrl && (prev.some((m) => m.imageUrl === imageUrl) || knownImageUrlsRef.current.has(imageUrl))) return prev;
      if (imageUrl) knownImageUrlsRef.current = new Set([...knownImageUrlsRef.current, imageUrl]);
      return [...prev, aiMsg];
    });
    aiPendingJobRef.current = null;
    setAiLoading(false);
    notifyIfAway();
  };
  const onGenError = (e) => {
    // Сервер сохранил сообщение-ошибку в истории ИИ — помечаем его известным,
    // иначе поллинг покажет ошибку повторно при следующей генерации и
    // погасит её спиннер.
    const failedId = e?.detail?.messageId;
    if (failedId) knownAssistantIdsRef.current = new Set([...knownAssistantIdsRef.current, failedId]);
    const errMsg = {
      id: Date.now() + 1,
      role: 'assistant',
      content: 'Ошибка генерации изображения. Попробуйте снова.',
      timestamp: new Date().toISOString(),
    };
    setAiMessages((prev) => (prev.some((m) => m.role === 'assistant' && m.content === errMsg.content) ? prev : [...prev, errMsg]));
    aiPendingJobRef.current = null;
    setAiLoading(false);
    if (activeAIChatRef.current) return;
    playBlaBlaSound();
    showBrowserNotification('AI Ассистент', 'Не удалось сгенерировать изображение', '__ai__');
  };
  useEffect(() => {
    window.addEventListener('ai_generation_done', onGenDone);
    window.addEventListener('ai_generation_error', onGenError);
    return () => {
      window.removeEventListener('ai_generation_done', onGenDone);
      window.removeEventListener('ai_generation_error', onGenError);
    };
  }, []);

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const sub = await api.post('/ai/subscribe');
      await api.post('/ai/subscribe/confirm', { sessionId: sub.data.sessionId });
      setShowSubscribe(false);
      await fetchAiLimit();
      alert('✅ Подписка на безлимитную генерацию оформлена на 1 месяц');
    } catch (e) {
      alert('Не удалось оформить подписку. Попробуйте ещё раз.');
    }
    setSubscribing(false);
  };

  const handleAIKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAISend(); }
  };

  const handleForwardMessage = (msg) => {
    setForwardItem({ type: 'message', messageId: msg.id, sourceChatId: msg.chatId });
  };

  const handleForwardAIImage = async (msg) => {
    await fetchChats();
    setForwardItem({ type: 'ai', dataUrl: msg.imageUrl });
  };

  const forwardToChat = async (targetChatId) => {
    if (!forwardItem || !targetChatId) return;
    setForwarding(true);
    try {
      if (forwardItem.type === 'message') {
        await forwardMessage(targetChatId, forwardItem.sourceChatId, forwardItem.messageId);
      } else if (forwardItem.type === 'ai') {
        const resp = await fetch(forwardItem.dataUrl);
        const blob = await resp.blob();
        const formData = new FormData();
        formData.append('file', blob, `ai_image_${Date.now()}.png`);
        const uploadRes = await api.post('/files/upload', formData);
        const url = uploadRes.data?.url;
        if (url) {
          await sendMessage(targetChatId, url, 'IMAGE');
        }
      }
      setForwardItem(null);
    } catch (e) {
      console.error('Forward failed:', e);
      alert('Не удалось переслать сообщение');
    }
    setForwarding(false);
  };

  const renderForwardModal = () => {
    if (!forwardItem) return null;
    return (
      <div className="modal-overlay" onClick={() => setForwardItem(null)}>
        <div className="modal user-info-modal" onClick={(e) => e.stopPropagation()}>
          <div className="user-info-header">
            <h3 style={{ margin: 0 }}>Переслать в чат</h3>
            <button className="modal-close-btn" onClick={() => setForwardItem(null)}>✕</button>
          </div>
          <div className="user-info-body">
            {chats.filter((c) => c.id !== (forwardItem.type === 'message' ? forwardItem.sourceChatId : activeChat)).map((c) => {
              const other = c.participants?.find((p) => p.userId !== user?.id);
              const name = c.name || other?.displayName || 'Чат';
              return (
                <button key={c.id} className="forward-chat-item" disabled={forwarding}
                  onClick={() => forwardToChat(c.id)}>
                  <span className="forward-chat-avatar">{getAvatarEmoji(name)}</span>
                  <span>{name}</span>
                </button>
              );
            })}
            {chats.filter((c) => c.id !== (forwardItem.type === 'message' ? forwardItem.sourceChatId : activeChat)).length === 0 && (
              <div className="user-info-status muted">Нет доступных чатов для пересылки</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (activeAIChat) {
    return (
      <div className="chat-window">
        <div className="chat-header">
          <div className="ai-header-avatar">🤖</div>
          <div className="chat-header-info">
            <div className="chat-header-name">AI Ассистент</div>
            <div className="chat-header-status">Генерация изображений</div>
          </div>
        </div>
        <div className="chat-messages">
          {aiMessages.length === 0 && (
            <div className="empty-state">
              <h2>🤖 AI Ассистент</h2>
              <p>Опишите изображение, и я сгенерирую его для вас</p>
            </div>
          )}
          {aiMessages.map((msg) => (
            msg.role === 'system' ? (
              <div key={msg.id} className="ai-limit-banner">
                <span>{msg.content}</span>
                <button className="ai-subscribe-btn" onClick={() => window.open(MASHAGPT_URL, '_blank', 'noopener,noreferrer')}>
                  Продолжить генерацию картинок в MashaGPT
                </button>
              </div>
            ) : (
              <div key={msg.id} className={`message ${msg.role === 'user' ? 'own' : 'other'}`}>
                {msg.role === 'assistant' && msg.imageUrl && (
                  <div className="ai-generated-image">
                    <img src={absUrl(msg.imageUrl)} alt={msg.content} className="msg-image" />
                    <button className="ai-forward-btn" onClick={() => handleForwardAIImage(msg)} title="Переслать в чат">➡ Переслать</button>
                  </div>
                )}
                {msg.role === 'assistant' && !msg.imageUrl && (
                  <div>{msg.content}</div>
                )}
                {msg.role === 'user' && <div>{msg.content}</div>}
                <div className="message-meta">
                  <span>{new Date(msg.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            )
          ))}
          {aiLoading && (
            <div className="message other">
              <div className="ai-typing">⏳ Изображение в процессе генерации. Это займёт до 2 минут. Можно не ждать в чате — когда картинка будет готова, придёт уведомление, и она появится здесь.</div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        {!aiLimit.unlimited && aiLimit.limited && !aiMessages.some((m) => m.role === 'system') && (
          <div className="ai-limit-banner">
            <span>{AI_LIMIT_TEXT}</span>
            <button className="ai-subscribe-btn" onClick={() => window.open(MASHAGPT_URL, '_blank', 'noopener,noreferrer')}>
              Продолжить генерацию картинок в MashaGPT
            </button>
          </div>
        )}
        {!aiLimit.unlimited && !aiLimit.limited && (
          <div className="ai-limit-counter">Осталось бесплатных генераций в месяц: {aiLimit.remaining}/{aiLimit.monthlyLimit || 5}</div>
        )}
        {aiLimit.unlimited && (
          <div className="ai-limit-counter unlimited">♾ Безлимитная генерация активна{aiLimit.unlimitedUntil ? ` до ${new Date(aiLimit.unlimitedUntil).toLocaleDateString('ru-RU')}` : ''}</div>
        )}
        {aiModels.length > 0 && (
          <div className="ai-model-row">
            <label>🎨 Модель картинок:</label>
            <select
              value={aiModel}
              onChange={(e) => { setAiModel(e.target.value); localStorage.setItem('trandi_ai_model', e.target.value); }}
            >
              {aiModels.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="chat-input-area">
          <button className="input-btn" onClick={() => setShowEmoji(!showEmoji)}>😊</button>
          <textarea value={input} onChange={(e) => { setInput(e.target.value); emitTyping(activeChat); }}
            onKeyDown={handleAIKeyDown} placeholder="Опишите изображение..." rows={1} />
          <button className="send-btn" onClick={handleAISend} disabled={aiLoading}>➤</button>
        </div>
        {showEmoji && <EmojiPicker onSelect={(emoji) => setInput((p) => p + emoji)} onClose={() => setShowEmoji(false)} />}
        {showSubscribe && (
          <div className="modal-overlay" onClick={() => setShowSubscribe(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Подписка на безлимитную генерацию</h3>
              <p style={{ fontSize: '13px', color: 'var(--system-text, #666)', marginTop: '0' }}>
                199 ₽ / 30 дней. Платёжный шлюз будет подключён позже — сейчас это тестовая заглушка.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                <button className="ai-subscribe-btn" style={{ padding: '12px' }} onClick={handleSubscribe} disabled={subscribing}>
                  {subscribing ? 'Обработка...' : '💳 Оплатить 199 ₽'}
                </button>
                <button className="action-btn" style={{ padding: '10px', background: 'var(--hover-bg, #f0f0f0)', color: 'var(--text, #333)', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  onClick={() => setShowSubscribe(false)}>Отмена</button>
              </div>
            </div>
          </div>
        )}
        {renderForwardModal()}
      </div>
    );
  }

  if (!activeChat) {
    return <div className="chat-window empty"><div className="empty-state"><img src="/tryndi.png" className="empty-wordmark" alt="Трынди" /><p>Выберите чат для общения</p></div></div>;
  }

  const otherParticipant = chat?.participants?.find((p) => p.userId !== user?.id);
  const isSelfChat = chat?.name === 'Своё';
  const chatAvatar = chat?.avatarUrl ? absUrl(chat.avatarUrl) : (otherParticipant?.avatarUrl ? absUrl(otherParticipant.avatarUrl) : null);
  const chatName = chat?.name || otherParticipant?.displayName || 'Чат';
  const infoTargetId = otherParticipant?.userId || user?.id;

  let lastDateLabel = '';
  const historyWarning = chat?.name !== 'Своё' ? getHistoryWarning() : null;

  const headerStatus = () => {
    if (typingUserId) return 'Печатает...';
    if (chat?.isChannel) return '📢 Канал';
    if (chat?.isGroup) return '👥 Группа';
    if (isSelfChat) return 'Личные заметки';
    return isOnline ? '🟢 В сети' : '⚪ Не в сети';
  };

  return (
    <div className="chat-window" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
      <div className="chat-header">
        {isSelfChat ? (
          <div className="chat-avatar self-chat-avatar"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', background: '#c8e6c9', borderRadius: '50%', cursor: 'pointer' }}
            onClick={() => setShowUserInfo(true)}>
            ☁️
          </div>
        ) : chatAvatar ? (
          <img src={chatAvatar} alt="" className="chat-avatar" onClick={() => chat?.isChannel ? setShowChannelInfo(true) : chat?.isGroup ? setShowGroupInfo(true) : infoTargetId && setShowUserInfo(true)}
            onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
        ) : (
          <div className="chat-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', background: '#e8e8ff', borderRadius: '50%', cursor: 'pointer' }}
            onClick={() => chat?.isChannel ? setShowChannelInfo(true) : chat?.isGroup ? setShowGroupInfo(true) : infoTargetId && setShowUserInfo(true)}>
            {getAvatarEmoji(chatName)}
          </div>
        )}
        <div className="chat-header-info" style={{ cursor: 'pointer' }}
          onClick={() => chat?.isChannel ? setShowChannelInfo(true) : chat?.isGroup ? setShowGroupInfo(true) : infoTargetId && setShowUserInfo(true)}>
          <div className="chat-header-name">{chatName}</div>
          <div className="chat-header-status">{headerStatus()}</div>
        </div>
        <div className="chat-header-actions">
          {activeChat && (
            <button
              onClick={() => toggleMute(activeChat)}
              title={chat?.muted ? 'Включить уведомления' : 'Отключить уведомления'}
              className={chat?.muted ? 'muted-active' : ''}
            >
              {chat?.muted ? '🔕' : '🔔'}
            </button>
          )}
          {chat?.isGroup && !chat?.isChannel ? (
            <>
              <button onClick={() => window.startCall?.(null, chatName, 'audio', chat.participants, activeChat)} title="Аудиозвонок группе">📞</button>
              <button onClick={() => window.startCall?.(null, chatName, 'video', chat.participants, activeChat)} title="Видеозвонок группе">📹</button>
            </>
          ) : otherParticipant && !chat?.isChannel && (
            <>
              {(() => {
                const otherOffline = onlineUsers[otherParticipant.userId] === false;
                const offlineCls = otherOffline ? 'call-btn-offline' : '';
                const offlineAlert = otherOffline ? () => alert('Пользователь не в сети') : null;
                return (
                  <>
                    <button className={offlineCls} onClick={offlineAlert || (() => chat?.theyBlockedMe
                      ? alert('Данный пользователь добавил ваш аккаунт в блокировку')
                      : chat?.isBlocked
                        ? alert('Вы заблокировали этого пользователя. Разблокируйте его в настройках, чтобы совершать звонки.')
                        : window.startCall?.(otherParticipant.userId, chatName, 'audio', null, activeChat))} title="Аудиозвонок">📞</button>
                    <button className={offlineCls} onClick={offlineAlert || (() => chat?.theyBlockedMe
                      ? alert('Данный пользователь добавил ваш аккаунт в блокировку')
                      : chat?.isBlocked
                        ? alert('Вы заблокировали этого пользователя. Разблокируйте его в настройках, чтобы совершать звонки.')
                        : window.startCall?.(otherParticipant.userId, chatName, 'video', null, activeChat))} title="Видеозвонок">📹</button>
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>
      <div className="chat-messages" ref={chatMessagesRef} onScroll={(e) => {
        const el = e.target;
        setIsNearBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 120);
      }} style={user?.chatBgImage ? {
        backgroundImage: `url(${absUrl(user.chatBgImage)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      } : {}}>
        {historyWarning && (
          <div className="history-warning-banner">{historyWarning}</div>
        )}
        {pinnedList.length > 0 && (
          <div className="pinned-message-banner">
            <span className="pinned-message-icon">📌</span>
            <div className="pinned-message-content">
              <div className="pinned-message-title">Закреплённое сообщение</div>
              <div className="pinned-message-text">
                {pinnedList[0].isDeleted ? 'Сообщение удалено' : (pinnedList[0].attachments?.[0]?.fileName || pinnedList[0].content || 'Сообщение')}
              </div>
            </div>
            {(isChannelAdmin || pinnedList[0].senderId === user?.id) && (
              <button className="pinned-message-unpin" onClick={() => unpinMessage(activeChat, pinnedList[0].id)} title="Открепить">✕</button>
            )}
          </div>
        )}
        {chatMessages.map((msg) => {
          const dateLabel = getDateLabel(msg.createdAt);
          let showDateSeparator = false;
          if (dateLabel !== lastDateLabel) {
            showDateSeparator = true;
            lastDateLabel = dateLabel;
          }
          return (
            <React.Fragment key={msg.id}>
              {showDateSeparator && (
                <div className="date-separator">
                  <span>{dateLabel}</span>
                </div>
              )}
              <MessageBubble message={msg} chatId={activeChat} onUserClick={() => setShowUserInfo(true)} onForward={handleForwardMessage}
                channelName={chat?.isChannel ? (chat?.name || 'Канал') : null}
                channelAvatar={chat?.isChannel && chat?.avatarUrl ? absUrl(chat.avatarUrl) : null} />
            </React.Fragment>
          );
        })}
        {typingUserId && <div className="typing-indicator">Печатает...</div>}
        <div ref={messagesEndRef} />
      </div>
      {!isNearBottom && (
        <button
          className="chat-scroll-down-btn"
          onClick={() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            setIsNearBottom(true);
          }}
          title="К новым сообщениям"
        >⬇</button>
      )}
      {pendingFile && (
        <div className="file-preview-bar">
          <div className="file-preview-content">
            {pendingFile.type === 'IMAGE' && <img src={pendingFile.url} alt="" className="file-preview-media" />}
            {pendingFile.type === 'VIDEO' && <video src={pendingFile.url} className="file-preview-media" controls />}
            {pendingFile.type === 'AUDIO' && <audio src={pendingFile.url} controls />}
            {pendingFile.type === 'FILE' && <span className="file-preview-name">📎 {pendingFile.file.name}</span>}
            <span className="file-preview-name">{pendingFile.file.name}</span>
          </div>
          <div className="file-preview-actions">
            <button className="send-btn" onClick={confirmPendingFile}>➤</button>
            <button className="input-btn" onClick={() => setPendingFile(null)}>✕</button>
          </div>
        </div>
      )}
      {recording && (
        <div className="recording-bar">
          <span className="recording-dot"></span>
          <span>🎤 Запись голосового сообщения — {recordingSeconds}с</span>
          <button className="send-btn" onClick={stopRecording} title="Остановить и отправить">⏹</button>
        </div>
      )}
      <div className="chat-input-area">
        {mentionOpen && filteredMentions.length > 0 && (
          <div className="mention-dropdown">
            {filteredMentions.slice(0, 6).map((p) => (
              <div
                key={p.userId}
                className="mention-item"
                onMouseDown={(e) => { e.preventDefault(); insertMention(p); }}
              >
                <span className="mention-item-avatar">{p.avatarUrl ? <img src={absUrl(p.avatarUrl)} alt="" /> : (p.displayName?.[0] || p.username?.[0] || '?')}</span>
                <span className="mention-item-name">{p.displayName || p.username}</span>
              </div>
            ))}
          </div>
        )}
        {canWrite ? (
          <>
            <button className="input-btn" onClick={() => { setShowEmoji(!showEmoji); setShowSchedule(false); }}>😊</button>
            <button className="input-btn" onClick={() => fileInputRef.current?.click()} title="Файл/изображение/видео">📎</button>
            <button className="input-btn" onClick={() => { setShowSchedule(!showSchedule); setShowEmoji(false); }} title="Отложить отправку">⏰</button>
            <input type="file" hidden ref={fileInputRef} onChange={handleFileSelect} />
            <textarea ref={inputRef} value={input} onChange={(e) => { const v = e.target.value; setInput(v); emitTyping(activeChat); updateMentionState(v, e.target.selectionStart ?? v.length); }}
              onKeyDown={handleKeyDown} placeholder="Напишите сообщение..." rows={1} />
            <button className="send-btn" onClick={handleSend}>➤</button>
            <button className={`input-btn ${recording === 'audio' ? 'recording-active' : ''}`}
              onClick={() => (recording ? stopRecording() : startRecording('audio'))} title="Голосовое сообщение">🎤</button>
            <button className="input-btn"
              onClick={() => setShowVideoRecorder(true)} title="Видео-сообщение">🎥</button>
          </>
        ) : chat?.theyBlockedMe ? (
          <div className="channel-readonly">🚫 Данный пользователь добавил ваш аккаунт в блокировку</div>
        ) : chat?.isBlocked ? (
          <div className="channel-readonly">🚫 Чат заблокирован. Вы не можете отправлять сообщения.</div>
        ) : (
          <div className="channel-readonly">📢 Этот канал доступен только для чтения. Можно оставлять реакции.</div>
        )}
      </div>
      {showSchedule && canWrite && (
        <div className="schedule-bar">
          <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="schedule-input" />
          <button className="send-btn" onClick={handleScheduleSend} disabled={!scheduleAt || !input.trim()} title="Отложить отправку">⏰</button>
          <button className="input-btn" onClick={() => setShowSchedule(false)}>✕</button>
        </div>
      )}
      {showEmoji && <EmojiPicker onSelect={(emoji) => setInput((p) => p + emoji)} onSticker={async (url) => { await sendMessage(activeChat, url, 'STICKER'); }} onClose={() => setShowEmoji(false)} />}
      {showUserInfo && infoTargetId && (
        <UserInfoPanel userId={infoTargetId} chatId={activeChat} user={user} onClose={() => setShowUserInfo(false)} />
      )}
      {showChannelInfo && user && (
        <ChannelInfoPanel chatId={activeChat} user={user} onClose={() => setShowChannelInfo(false)} />
      )}
      {showGroupInfo && user && (
        <GroupInfoPanel chatId={activeChat} user={user} onClose={() => setShowGroupInfo(false)} />
      )}
      {showVideoRecorder && (
        <VideoRecorderModal
          chatId={activeChat}
          sendFileMessage={sendFileMessage}
          scheduledAt={showSchedule && scheduleAt ? new Date(scheduleAt).toISOString() : null}
          onSent={(scheduled) => {
            if (scheduled) {
              setScheduleAt('');
              setShowSchedule(false);
              alert('⏰ Сообщение сохранено в «Запланированные»');
            }
          }}
          onClose={() => setShowVideoRecorder(false)}
        />
      )}
      {renderForwardModal()}
    </div>
  );
}
